import { Plus, Printer, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ActiveFilter } from '@/lib/api-client';
import { cn } from '@/lib/utils';

interface ListToolbarProps {
  title: string;
  description?: string;
  newLabel: string;
  onNew: () => void;
  active?: ActiveFilter;
  onActiveChange?: (v: ActiveFilter) => void;
  search: string;
  onSearchChange: (v: string) => void;
}

/**
 * Cabecalho padrao das 3 paginas de configuracao. Botao primario azul
 * para criar; segmented control Ativos/Inativos/Todos; busca livre;
 * imprimir como placeholder por enquanto (window.print fallback).
 */
export function ListToolbar({
  title,
  description,
  newLabel,
  onNew,
  active,
  onActiveChange,
  search,
  onSearchChange,
}: ListToolbarProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-[var(--fg)] md:text-2xl">
            {title}
          </h1>
          {description && (
            <p className="mt-1 text-sm text-[var(--fg-muted)]">{description}</p>
          )}
        </div>
        <Button onClick={onNew} className="md:self-end">
          <Plus className="mr-2 h-4 w-4" />
          {newLabel}
        </Button>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        {active !== undefined && onActiveChange !== undefined && (
          <div
            className="inline-flex overflow-hidden rounded-md border border-[var(--border)]"
            role="tablist"
          >
            <SegBtn
              active={active === 'true'}
              onClick={() => onActiveChange('true')}
            >
              Ativos
            </SegBtn>
            <SegBtn
              active={active === 'false'}
              onClick={() => onActiveChange('false')}
            >
              Inativos
            </SegBtn>
            <SegBtn
              active={active === 'all'}
              onClick={() => onActiveChange('all')}
            >
              Todos
            </SegBtn>
          </div>
        )}

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--fg-muted)]" />
            <input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Buscar"
              className={cn(
                'h-9 w-full rounded-md border bg-transparent pl-8 pr-3 text-sm md:w-64',
                'border-[var(--border)] text-[var(--fg)] placeholder:text-[var(--fg-muted)]',
                'focus:outline-none focus:ring-2 focus:ring-[var(--accent)]',
              )}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
            title="Imprimir lista"
          >
            <Printer className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function SegBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 text-xs font-medium transition',
        active
          ? 'bg-[color-mix(in_oklch,var(--accent)_18%,transparent)] text-[var(--fg)]'
          : 'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] hover:text-[var(--fg)]',
      )}
    >
      {children}
    </button>
  );
}
